import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { HomeOutlined, BankOutlined, ContactsOutlined } from '@ant-design/icons';
import StartLayout from './layouts/StartLayout';
import DashboardLayout from './layouts/DashboardLayout';
import SignInPage, { SIGN_IN_PAGE_PATH } from './pages/auth/SignInPage';
import OverviewPage, { OVERVIEW_PAGE_PATH, OVERVIEW_PAGE_TITLE } from './pages/OverviewPage';
import ContactsPage, { CONTACTS_PAGE_PATH, CONTACTS_PAGE_TITLE } from './pages/contacts/ContactsPage';
import ConversationsPage, { CONVERSATIONS_MENU_TITLE, CONVERSATIONS_PAGE_PATH } from './pages/conversations/ConversationsPage';
import ActivityIcon from './pages/conversations/components/ActivityIcon';
import UsersPage, { USERS_PAGE_PATH } from './pages/users/UsersPage';
import SubscriptionsPage, { SUBSCRIPTIONS_PAGE_PATH } from './pages/subscriptions/SubscriptionsPage';
import SettingsPage, { SETTINGS_PAGE_PATH } from './pages/settings/SettingsPage';
import ReceptionistAiListPage, { RECEPTIONIST_AI_PAGE_PATH } from './pages/receptionist_ai/ReceptionistAiListPage';
import ReceptionistAiDetailPage, { RECEPTIONIST_AI_DETAIL_PAGE_PATH } from './pages/receptionist_ai/ReceptionistAiDetailPage';
import CompanyPage, { COMPANY_PAGE_PATH, COMPANY_PAGE_TITLE } from './pages/company/CompanyPage';
import CompanyBillingPage, { COMPANY_BILLING_PAGE_PATH } from './pages/company/CompanyBillingPage';
import CompanySettingsPage, { COMPANY_SETTINGS_PAGE_PATH } from './pages/company/CompanySettingsPage';
import SignUpPage, { SIGN_UP_PAGE_PATH } from './pages/auth/SignUpPage';
import { metadata } from '@leedpulse-monorepo/firebase-metadata';
import MobileRedirectPage, { MOBILE_REDIRECT_PAGE_PATH } from './pages/auth/MobileRedirectPage';
import PasswordResetPage, { PASSWORD_RESET_PAGE_PATH } from './pages/auth/PasswordResetPage';
import OAuthAuthorizePage, { OAUTH_AUTHORIZE_PAGE_PATH } from './pages/oauth/OAuthAuthorizePage';
import RequireSupervisor from './components/@shared/require-supervisor/RequireSupervisor';

export const navigationConfig = [
  {
    title: OVERVIEW_PAGE_TITLE,
    path: OVERVIEW_PAGE_PATH,
    icon: <HomeOutlined />,
    iconActive: <HomeOutlined />,
  },
  {
    title: CONVERSATIONS_MENU_TITLE,
    path: CONVERSATIONS_PAGE_PATH,
    icon: <ActivityIcon />,
    iconActive: <ActivityIcon />,
  },
  {
    title: CONTACTS_PAGE_TITLE,
    path: CONTACTS_PAGE_PATH,
    icon: <ContactsOutlined />,
    iconActive: <ContactsOutlined />,
  },
  {
    title: COMPANY_PAGE_TITLE,
    path: COMPANY_PAGE_PATH,
    icon: <BankOutlined />,
    iconActive: <BankOutlined />,
    dividerBefore: true,
  },
];

export const PUBLIC_ROUTES = [
  {
    path: SIGN_IN_PAGE_PATH,
    element: <SignInPage />,
  },
  {
    path: PASSWORD_RESET_PAGE_PATH,
    element: <PasswordResetPage />,
  },
];

// Keeps the subscription and the query (?tab=...) of the former receptionist links
const LegacyReceptionistAiRedirect: React.FC = () => {
  const { subscriptionId } = useParams();
  const { search } = useLocation();
  const path = subscriptionId ? `${RECEPTIONIST_AI_PAGE_PATH}/${subscriptionId}` : RECEPTIONIST_AI_PAGE_PATH;

  return <Navigate to={`${path}${search}`} replace />;
};

const AppRouter: React.FC = () => {
  return (
    <Routes>
      {/* Public page */}
      <Route path={MOBILE_REDIRECT_PAGE_PATH} element={<MobileRedirectPage />} />

      {/* OAuth consent (bare public route — handles its own Firebase auth check) */}
      <Route path={OAUTH_AUTHORIZE_PAGE_PATH} element={<OAuthAuthorizePage />} />

      <Route element={<StartLayout />}>
        {PUBLIC_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
        {/* Disabled in production mode */}
        {metadata('frontendleedflow').databaseId.includes('development') && (
          <Route path={SIGN_UP_PAGE_PATH} element={<SignUpPage />} />
        )}
      </Route>

      <Route element={<DashboardLayout />}>
        <Route path="/" element={<Navigate to={OVERVIEW_PAGE_PATH} replace />} />

        {/* Company users pages */}
        <Route path={OVERVIEW_PAGE_PATH} element={<OverviewPage />} />

        {/* Open to any authenticated user with an active company: the backend enforces the
            real boundary (supervisor, or the member the subscription is assigned to) */}
        <Route path={RECEPTIONIST_AI_DETAIL_PAGE_PATH} element={<ReceptionistAiDetailPage />} />
        <Route path={CONTACTS_PAGE_PATH} element={<ContactsPage />} />
        <Route path={CONVERSATIONS_PAGE_PATH} element={<ConversationsPage />} />

        {/* Company supervisors only pages */}
        <Route path={USERS_PAGE_PATH} element={<RequireSupervisor><UsersPage /></RequireSupervisor>} />
        <Route path={SUBSCRIPTIONS_PAGE_PATH} element={<RequireSupervisor><SubscriptionsPage /></RequireSupervisor>} />
        <Route path={RECEPTIONIST_AI_PAGE_PATH} element={<ReceptionistAiListPage />} />
        <Route path={COMPANY_BILLING_PAGE_PATH} element={<RequireSupervisor><CompanyBillingPage /></RequireSupervisor>} />
        <Route path={COMPANY_SETTINGS_PAGE_PATH} element={<RequireSupervisor><CompanySettingsPage /></RequireSupervisor>} />
        <Route path={COMPANY_PAGE_PATH} element={<CompanyPage />} />
        <Route path={SETTINGS_PAGE_PATH} element={<SettingsPage />} />

        {/* Former routes, before they moved under "Mon entreprise" */}
        <Route path="/users" element={<Navigate to={USERS_PAGE_PATH} replace />} />
        <Route path="/numbers" element={<Navigate to={SUBSCRIPTIONS_PAGE_PATH} replace />} />
        <Route path="/receptionniste-ia" element={<LegacyReceptionistAiRedirect />} />
        <Route path="/receptionniste-ia/:subscriptionId" element={<LegacyReceptionistAiRedirect />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRouter;
